import { isOpenableLeadStatus, type PartnerCockpitOfferRequest } from '@houzy/schemas/partner-cockpit/offer-requests';
import Stack from '@mui/material/Stack';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { OfferRequestCard, type OfferRequestCardChip } from '../../../components/cards/OfferRequestCard';
import { reimbursementOutcome } from '../../reimbursement';
import { formatCreditsLabel, offerRequestLeadStatusLabel, offerRequestTimingLabel, offerRequestTitle } from '../labels';
import { formatOfferRequestSubtitle } from '../utils/formatOfferRequestSubtitle';
import { OfferRequestsEmptyState } from './OfferRequestsEmptyState';
import { ReimbursementStatusChip } from './ReimbursementStatusChip';

export type OfferRequestsListProps = {
    offerRequests: PartnerCockpitOfferRequest[];
    onSelectOfferRequest?: (offerRequest: PartnerCockpitOfferRequest) => void;
    onDeclineOfferRequest?: (offerRequest: PartnerCockpitOfferRequest) => void;
    declineDisabled?: boolean;
    // Forwarded test/clock anchor so stories and tests stay deterministic.
    now?: Date;
};

function chipsForOfferRequest(t: TFunction, req: PartnerCockpitOfferRequest): OfferRequestCardChip[] {
    const chips: OfferRequestCardChip[] = [
        { id: `${req.uuid}-timing`, label: offerRequestTimingLabel(t, req.timeConstraint) },
    ];
    if (req.credits !== undefined) {
        chips.push({ id: `${req.uuid}-credits`, label: formatCreditsLabel(t, req.credits) });
    }
    return chips;
}

export function OfferRequestsList({
    offerRequests,
    onSelectOfferRequest,
    onDeclineOfferRequest,
    declineDisabled = false,
    now,
}: OfferRequestsListProps): ReactElement {
    const { t, i18n } = useTranslation();

    if (offerRequests.length === 0) {
        return (
            <OfferRequestsEmptyState title={t('OFFER_REQUESTS.EMPTY.TITLE')} hint={t('OFFER_REQUESTS.EMPTY.HINT')} />
        );
    }

    return (
        <Stack spacing={1} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {offerRequests.map((req) => {
                const title = offerRequestTitle(t, req.title, req.workCategoryKeys, req.referenceId, i18n.language);
                const badge = offerRequestLeadStatusLabel(t, req.leadStatus);
                const openable = isOpenableLeadStatus(req.leadStatus);
                const declineAction =
                    req.canDecline && onDeclineOfferRequest
                        ? {
                              label: t('OFFER_REQUESTS.DETAIL.DECLINE'),
                              onClick: (): void => onDeclineOfferRequest(req),
                              disabled: declineDisabled,
                          }
                        : undefined;
                const outcome = reimbursementOutcome(req.reimbursement);
                return (
                    <li
                        key={req.uuid}
                        aria-label={t('OFFER_REQUESTS.CARD.LIST_ITEM_LABEL', {
                            title,
                            credits: req.credits !== undefined ? formatCreditsLabel(t, req.credits) : '',
                        })}>
                        <OfferRequestCard
                            title={title}
                            subtitle={formatOfferRequestSubtitle(t, req, now)}
                            chips={chipsForOfferRequest(t, req)}
                            badge={badge}
                            muted={!openable}
                            declineAction={declineAction}
                            statusChip={
                                outcome && (
                                    <ReimbursementStatusChip
                                        outcome={outcome}
                                        reimbursedCredits={req.reimbursedCredits}
                                    />
                                )
                            }
                            onClick={
                                onSelectOfferRequest && openable ? (): void => onSelectOfferRequest(req) : undefined
                            }
                        />
                    </li>
                );
            })}
        </Stack>
    );
}
